//
// Imports
//

import Sortable from "sortablejs";

import * as BrowserUtilities from "@donutteam/browser-utilities";
import { createNotice } from "@donutteam/document-builder-components/src/client.js";

import { RequestBody, ResponseBodySchema } from "../../../api/routes/v6/release-media/update-sort-order.schemas.js";

//
// Locals
//

type UpdateSortOrdersOptions =
{
	apiUrl: string;
	formElement: HTMLElement;
	release_id: number;
	releaseMediaGrid: HTMLElement;
};

async function updateSortOrders(options: UpdateSortOrdersOptions)
{
	const requestBody: RequestBody =
	{
		release_id: options.release_id,
		media: getMedia(options.releaseMediaGrid),
	};

	const response = await fetch(options.apiUrl + "/v6/release-media/update-sort-order",
		{
			method: "POST",
			credentials: "include",
			headers:
			{
				"Content-Type": "application/json",
			},
			body: JSON.stringify(requestBody),
		});

	const responseJson = await response.json();

	const responseBody = ResponseBodySchema.parse(responseJson);

	options.formElement.querySelectorAll(".component-notice").forEach((existingNotice) => existingNotice.remove());

	const notices = responseBody.success
		? [ createNotice({ type: "success", message: "Sort order saved successfully.", dismissible: true }) ]
		: responseBody.errors.map(
			(error) =>
			{
				return createNotice({ type: "warning", message: error.message, dismissible: true });
			});

	for (const notice of notices)
	{
		options.formElement.prepend(notice);
	}

	options.formElement.scrollTo();
}

function getMedia(gridElement: HTMLElement): RequestBody["media"]
{
	const media: RequestBody["media"] = [];

	const releaseMediaGridItems = Array.from(gridElement.querySelectorAll(".component-release-media-grid-item") as NodeListOf<HTMLElement>);

	for (const releaseMediaGridItem of releaseMediaGridItems)
	{
		const type = releaseMediaGridItem.dataset["type"];

		if (type != "image" && type != "video")
		{
			throw new Error("[UpdateReleaseMediaSortOrderForm] Invalid type: " + type);
		}

		const id = parseInt(releaseMediaGridItem.dataset["id"] as string);

		if (isNaN(id))
		{
			throw new Error("[UpdateReleaseMediaSortOrderForm] Invalid ID: " + releaseMediaGridItem.dataset["id"]);
		}

		media.push(
			{
				type,
				id,
			});
	}

	return media;
}

//
// Component
//

export function initialiseUpdateReleaseMediaSortOrderForm(updateReleaseMediaSortOrderForm: HTMLElement)
{
	const apiUrl = BrowserUtilities.ElementClientLib.getStringDataOrThrow(updateReleaseMediaSortOrderForm, "apiUrl");

	const release_id = BrowserUtilities.ElementClientLib.getIntegerDataOrThrow(updateReleaseMediaSortOrderForm, "release");

	const releaseMediaGrid = BrowserUtilities.ElementClientLib.getElementOrThrow(updateReleaseMediaSortOrderForm, ".component-release-media-grid");

	const submitButton = BrowserUtilities.ElementClientLib.getElementOrThrow(updateReleaseMediaSortOrderForm, ".component-button") as HTMLButtonElement;

	new Sortable(releaseMediaGrid,
		{
			animation: 150,
		});

	submitButton.addEventListener("click",
		async (event) =>
		{
			event.preventDefault();

			try
			{
				submitButton.setAttribute("disabled", "");

				await updateSortOrders(
					{
						apiUrl,
						formElement: updateReleaseMediaSortOrderForm,
						release_id,
						releaseMediaGrid,
					});
			}
			catch (error)
			{
				console.error("[UpdateReleaseMediaSortOrderForm] Failed to submit changes:", error);
			}
			finally
			{
				submitButton.removeAttribute("disabled");
			}
		});

	updateReleaseMediaSortOrderForm.classList.add("initialised");
}

export function initialiseUpdateReleaseMediaSortOrderForms()
{
	const updateReleaseMediaSortOrderForms = document.querySelectorAll<HTMLElement>(".component-update-release-media-sort-order-form:not(.initialised)");

	console.log("[UpdateReleaseMediaSortOrderForm] Initialising " + updateReleaseMediaSortOrderForms.length + " instances..");

	for (const updateReleaseMediaSortOrderForm of updateReleaseMediaSortOrderForms)
	{
		try
		{
			initialiseUpdateReleaseMediaSortOrderForm(updateReleaseMediaSortOrderForm);
		}
		catch (error)
		{
			console.error("[UpdateReleaseMediaSortOrderForm] Error initialising:", updateReleaseMediaSortOrderForm, error);
		}
	}
}
